<template>
  <div class="auth-layout-wrapper">
    <div class="monogram-overlay"></div>
    <div class="scroll-wrapper">
      <div class="auth-container">
        <div class="auth-card-top">
          <div class="lang-switcher-auth">
            <button class="lang-btn" :class="{ active: currentLanguage === 'it' }" @click="setLanguage('it')">IT</button>
            <button class="lang-btn" :class="{ active: currentLanguage === 'en' }" @click="setLanguage('en')">EN</button>
          </div>
          <router-link :to="configStore.isCountdownMode ? '/countdown' : '/'" class="btn-close" :title="t('account.personal_data.btn.cancel')">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </router-link>
        </div>
        <div class="auth-logo">
          <router-link :to="configStore.isCountdownMode ? '/countdown' : '/'" class="logo-link">
            <img src="@/assets/images/logo.png" :alt="t('header.nav.logo_alt')" class="logo">
          </router-link>
        </div>
        <div class="auth-page-content">
          <router-view />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useLanguage } from '@/composables/useLanguage'
import { useConfigStore } from '@/stores/configStore'

const { t, currentLanguage, setLanguage } = useLanguage()
const configStore = useConfigStore()
</script>

<style scoped>
.lang-switcher-auth {
  display: inline-flex;
  background: var(--gray-100, #f5f5f5);
  border-radius: 20px;
  padding: 3px;
  gap: 2px;
}
.lang-btn {
  border: none;
  background: transparent;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 6px 12px;
  border-radius: 16px;
  cursor: pointer;
  color: #888888;
  transition: all 0.2s ease;
}
.lang-btn.active {
  background: var(--dark-teal);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(0,140,149,0.35);
}
.lang-btn:not(.active):hover {
  color: var(--dark-teal);
}

.auth-layout-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  height: 100dvh;
  background-color: #ffffff;
  overflow: hidden;
  display: flex;
}

.monogram-overlay {
  position: fixed;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background-image: url("@/assets/images/logo.png");
  background-size: 120px auto;
  background-repeat: repeat;
  opacity: 0.12;
  transform: rotate(-15deg);
  z-index: 1;
  pointer-events: none;
}

.scroll-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 40px 20px;
  padding-top: calc(40px + env(safe-area-inset-top));
  padding-bottom: calc(40px + env(safe-area-inset-bottom));
  padding-left: calc(20px + env(safe-area-inset-left));
  padding-right: calc(20px + env(safe-area-inset-right));
  z-index: 2;
  -webkit-overflow-scrolling: touch;
}

.auth-container {
  width: 100%;
  max-width: 460px;
  background: white;
  padding: 40px 44px 36px;
  position: relative;
  z-index: 10;
  margin: 20px auto 40px;
  flex-shrink: 0;
  border-radius: 14px;
  box-shadow:
    0 1px 2px rgba(0,0,0,0.04),
    0 12px 32px -8px rgba(0,0,0,0.10),
    0 24px 64px -24px rgba(0,140,149,0.18);
  animation: rise 0.5s cubic-bezier(0.16,1,0.3,1);
}

@keyframes rise {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

.auth-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.btn-close {
  border: none;
  background: transparent;
  color: #999999;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: color 0.2s ease;
  line-height: 0;
}
.btn-close:hover { 
  color: #111111;
  transform: scale(1.1);
}

.auth-logo {
  text-align: center;
  margin: 18px 0 6px;
  display: flex;
  justify-content: center;
}

.logo-link {
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo {
  height: 60px;
  width: auto;
  object-fit: contain;
}

.auth-page-content {
  position: relative;
  z-index: 10;
}

@media (max-width: 768px) {
  .scroll-wrapper {
    padding: 24px 16px;
    padding-top: calc(24px + env(safe-area-inset-top));
    padding-bottom: calc(24px + env(safe-area-inset-bottom));
  }

  .auth-container {
    padding: 28px 22px 24px;
    margin: 16px auto 28px;
    border-radius: 12px;
  }

  .auth-logo {
    margin-top: 12px;
    margin-bottom: 12px;
  }

  .logo {
    height: 44px;
  }
}

@media (max-width: 380px) {
  .auth-container {
    padding: 24px 16px 20px;
  }
  .lang-btn {
    font-size: 10px;
    padding: 5px 10px;
  }
}

@media (max-height: 700px) {
  .scroll-wrapper {
    padding: 16px;
  }
  .auth-container {
    margin: 8px auto 24px;
  }
}

@media (max-height: 550px) {
  .scroll-wrapper {
    padding: 8px 12px;
    padding-top: calc(8px + env(safe-area-inset-top));
    padding-bottom: calc(8px + env(safe-area-inset-bottom));
  }
  .auth-container {
    padding: 20px 18px 16px;
    margin: 0 auto 16px;
    border-radius: 10px;
  }
  .auth-logo {
    margin: 6px 0;
  }
  .logo {
    height: 36px;
  }
}
</style>
